import React, { useState, useEffect, useContext } from "react";
import Styles from "../styles/Posts.module.css";
import { postsAPI } from "../services/api";
import { UserContext } from "./context/user/UserContext";
import { useDispatch, useSelector } from "react-redux";
import {
  setPosts,
  replacePosts,
  removePost,
  updatePost,
} from "../redux/slices/PostsSlice";
import Post from "./Post";
import Loader from "./Loader";
import Toast from "./Toast";
import ConfirmModal from "./ConfirmModal";

const Posts = () => {
  const { userData } = useContext(UserContext);
  const token = userData?.token;
  const dispatch = useDispatch();
  const posts = useSelector((state) => state.posts.posts);

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    postId: null,
  });

  useEffect(() => {
    if (!token) return;

    let active = true;
    setLoading(true);

    postsAPI
      .getAllPosts(token, page, query)
      .then((response) => {
        if (!active) return;
        const pages = Math.max(1, Number(response.totalPages) || 1);
        setTotalPages(pages);
        if (page === 1) {
          dispatch(replacePosts(response.data || []));
        } else {
          dispatch(setPosts(response.data || []));
        }
      })
      .catch((error) => {
        if (!active) return;
        setToast({
          message: `Failed to load posts: ${error.message}`,
          type: "error",
        });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [dispatch, page, query, refreshKey, token]);

  const applySearch = () => {
    setPage(1);
    setQuery(search.trim());
  };

  const clearSearch = () => {
    setSearch("");
    setQuery("");
    setPage(1);
  };

  const handleDeleteClick = (postId) => {
    setConfirmModal({ isOpen: true, postId });
  };

  const handleDeleteConfirm = async () => {
    const postId = confirmModal.postId;
    setToast({ message: "Deleting post...", type: "info" });

    try {
      await postsAPI.deletePost(token, postId);
      dispatch(removePost(postId));
      setToast({ message: "Post deleted successfully!", type: "success" });

      if (posts.length === 1 && page > 1) {
        setPage((value) => value - 1);
      } else {
        setRefreshKey((value) => value + 1);
      }
    } catch (error) {
      setToast({
        message: `Failed to delete post: ${error.message}`,
        type: "error",
      });
    }
  };

  const handleUpdate = async (postId, updates) => {
    setToast({ message: "Updating post...", type: "info" });

    try {
      await postsAPI.updatePost(token, postId, updates);
      dispatch(updatePost({ id: postId, updates }));
      setToast({ message: "Post updated successfully!", type: "success" });
    } catch (error) {
      setToast({
        message: `Failed to update post: ${error.message}`,
        type: "error",
      });
    }
  };

  return (
    <div className={Styles.container}>
      <div className={Styles.heading}>
        <h2>POSTS</h2>
      </div>

      <div className={Styles.inputContainer}>
        <input
          type="text"
          value={search}
          className={Styles.input}
          placeholder="Search title or description"
          aria-label="Search posts"
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") applySearch();
          }}
        />
        {search && (
          <button
            type="button"
            className={Styles.clear_button}
            onClick={clearSearch}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          className={Styles.search_button}
          onClick={applySearch}
        >
          Search
        </button>
      </div>

      <div className={Styles.posts_list}>
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Post ID</th>
              <th>Title</th>
              <th>Description</th>
              <th>
                Reasons <br /> (Reporting)
              </th>
              <th>Username</th>
              <th>User ID</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {posts.map((post, index) => (
              <Post
                key={post.id}
                post={post}
                index={index}
                page={page}
                handleDelete={handleDeleteClick}
                handleUpdate={handleUpdate}
              />
            ))}
            {!loading && posts.length === 0 && (
              <tr>
                <td colSpan="8" className={Styles.empty_state}>
                  No posts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className={Styles.pagination}>
        <button
          type="button"
          onClick={() => setPage((value) => Math.max(1, value - 1))}
          disabled={loading || page <= 1}
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
          disabled={loading || page >= totalPages}
        >
          Next
        </button>
      </div>

      {loading && <Loader text="Loading posts" />}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, postId: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Location?"
        message="Are you sure you want to delete this location? This action cannot be undone and will permanently remove this location from the database."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Posts;
